// The data manifest for THIS dashboard (IA_ABSORPTION_PLAN Phase 3 step 8): every table
// issue-analysis loads into DuckDB, where it comes from, and the DDL around it. All project SQL
// for the load lives here; `@rdrive/data-loader` (`createDataLoader`, built over this by the
// dashboard — dashboard.config.js's `data.manifest`) holds none — PLATFORM.md §3.2 rule 2.
//
// The 15 boot tables are the inline `apiRequests` array the root component's getData() held until
// this step: same paths, query strings, formats, table names and order, the paged issues endpoint,
// and the eager `status_history` (issue-analysis has no lazy table). `{projectCode}` is filled
// from the dashboard's live settings at load time. The welcome dialog's "N of 15" comes from the
// loader's progress (the root component's hand-counted `totalApiRequests = 15` is gone).
//
// What did NOT move here, and why:
//  - the root component's `CREATE TABLE IF NOT EXISTS item_templates (Id VARCHAR, …)` placeholder.
//    It went through the GATED `conn.query` right after `setDataLoaded(false)` had shut the gate,
//    so it never executed (DuckDBService answered with an empty result). Carrying it as `ddl.pre`
//    (which runs ungated, through `conn.loadData`) would create an EMPTY item_templates that the
//    parquet load's `CREATE TABLE IF NOT EXISTS … AS SELECT` then skips — so it is dropped, not
//    moved.
//  - The `UPDATE … SET issueClientId = 'null'` null replacement: it was guarded by
//    `table === 'items'`, and issue-analysis has no `items` table (its fact table is `issues`), so
//    it never ran.
//  - The per-table `console.log('[…] Fetching parquet for <table> from: <path>')` lines (the
//    loader logs failures itself) and the JSON loader's `[DuckDB] …` debug lines (they printed the
//    proxy URL, scoped token included) — both were app/IA-copy logging, not transport.

/** issue-analysis's issue fact table: the BI-Issue tag pivot, one column per field tag. */
const ISSUE_FIELD_TAGS = [
  'BI-Issue-Type',
  'BI-Issue-Category',
  'BI-Issue-Item',
  'BI-Responsible-Party',
  'BI-Photo',
  'BI-Priority',
  'BI-DueBy',
  'BI-Response-Photo',
  'BI-Issue-Description',
];

/**
 * The three JSON tables carry the `Accept` header issue-analysis's own DuckDBService copy sent
 * (`@rdrive/data-duckdb`'s JSON loader sends none by default) — so the requests stay byte-identical
 * to the Phase-0 transport baselines (tooling/smoke/baselines/ia-transport-2026-10-05-*.txt).
 */
const JSON_ACCEPT = { Accept: 'application/json' };

/**
 * `status_history_temporal` is DERIVED, not fetched: one row per status interval
 * (validFrom/validTo) from `status_history` joined to `issues`. It needs BOTH tables, so it is
 * `ddl.post` (after every boot table), exactly where the root component built it — verbatim, incl. the plain
 * `CREATE TABLE` (issue-analysis loads once per page life; there is no full-reload cycle).
 */
const STATUS_HISTORY_TEMPORAL = `
      CREATE TABLE status_history_temporal AS
        WITH ordered AS (
          SELECT
              sht.subItemId,
              sht.statusId,
              i.issueLocationId as locationId,
              sht.createdByTeamId,
              sht.createdByUserId,
              i.issueTemplateId as templateId,
              sht.createdDate                      AS validFrom,
              LEAD(sht.createdDate)
                OVER (PARTITION BY sht.subItemId ORDER BY sht.createdDate)
                                                    AS validTo
          FROM status_history sht
          JOIN issues i ON sht.subItemId = i.id
        )
        SELECT
            subItemId as subItemId,
            statusId as statusId,
            locationId as locationId,
            createdByTeamId as createdByTeamId,
            createdByUserId as createdByUserId,
            templateId as templateId,
            validFrom,
            validTo,
            CASE
                WHEN validTo IS NOT NULL
                THEN DATEDIFF('minute', validFrom, validTo)
                ELSE DATEDIFF('minute', validFrom,
                              CAST(CURRENT_TIMESTAMP AS TIMESTAMP))
            END AS durationMinutes
        FROM ordered
        ORDER BY subItemId, validFrom;   -- keeps IDs highly compressible
    `;

/**
 * Re-ranks `status_templates.sort` once the boot tables are in: drafts first, then initial
 * statuses, terminal ones last, terminal-but-not-passed pinned at 50 — verbatim from the root component's old load.
 */
const STATUS_TEMPLATES_SORT = `
      UPDATE status_templates
      SET sort =
          CASE
              WHEN isTerminal AND NOT isPassed THEN 50
              ELSE sort +
                  CASE WHEN isDraft THEN -100 ELSE 0 END +
                  CASE WHEN isInitial THEN -50 ELSE 0 END +
                  CASE WHEN isTerminal THEN 100 ELSE 0 END
          END;
    `;

export const manifest = {
  tables: [
    {
      name: 'issues',
      path: '/api/{projectCode}/reporting/issues/tag-pivot-as-parquet/BI-Issue',
      query: { fieldTags: ISSUE_FIELD_TAGS },
      format: 'parquet',
      paged: true,
    },
    {
      name: 'status_history',
      path: '/api/{projectCode}/reporting/items/status-history-short',
      query: { templateTags: 'BI-Issue' },
      format: 'parquet',
    },
    { name: 'item_templates', path: '/api/{projectCode}/reporting/project-item-templates', format: 'parquet' },
    { name: 'categories', path: '/api/categories', format: 'parquet' },
    { name: 'status_templates', path: '/api/{projectCode}/reporting/items/item-status-templates', format: 'parquet' },
    { name: 'drawings', path: '/api/{projectCode}/drawings', format: 'parquet' },
    { name: 'locations', path: '/api/{projectCode}/locations', format: 'parquet' },
    { name: 'teams', path: '/api/{projectCode}/teams', format: 'parquet' },
    { name: 'team_users', path: '/api/{projectCode}/bi/teams', format: 'parquet' },
    { name: 'users', path: '/api/{projectCode}/users', format: 'parquet' },
    { name: 'documents', path: '/api/{projectCode}/documents', format: 'parquet' },
    { name: 'current_user', path: '/api/users/current', format: 'json', headers: JSON_ACCEPT },
    { name: 'form_templates', path: '/api/{projectCode}/bi/form-templates', format: 'parquet' },
    { name: 'permission_rules', path: '/api/{projectCode}/users/current/permission-rules', format: 'json', headers: JSON_ACCEPT },
    { name: 'company', path: '/api/company', format: 'json', headers: JSON_ACCEPT },
  ],
  ddl: {
    post: [STATUS_HISTORY_TEMPORAL, STATUS_TEMPLATES_SORT],
  },
};
